<script setup lang="ts">
import type { DatalayerReadyData, UserData } from '~~/interfaces/tracking'
import useUserStore from '~~/app/store/user'

const config = useRuntimeConfig()

const { S3_ASSETS_URL, S3_LEGACY_URL, APPLICATION_URL, CHARGEMAP_URL } =
    config.public
const GTM_ID = config.public.GTM_ID as string
const route = useRoute()
const { billingAddress } = useUserStore()

const locales = ref(['de', 'en', 'es', 'fr', 'it', 'nl', 'pt'])
const isDesktop = ref(true)

const userId = computed<string>(() => useUserSession().user?.value?.id || '0')

const { href } = useRequestURL()

const sendDataLayerReadyEvent = () => {
    const user_data: UserData = {
        user_id: Number(userId.value) || 0,
        email_address: billingAddress?.email || '',
        ...(billingAddress?.countryCode && {
            address: {
                first_name: billingAddress.firstName || '',
                last_name: billingAddress.name || '',
                city: billingAddress.city,
                postal_code: billingAddress.postalCode,
                country: billingAddress.countryCode
            }
        })
    }

    const pathParts = route.path.split('/')

    const dataToSend: DatalayerReadyData = {
        event: 'datalayer_ready',
        content_group: `${pathParts[1]}-${pathParts[2]}`,
        language: useLanguage(),
        country: useCountry(),
        ...(Number(userId.value) && { user_data })
    }

    ;(window as any).dataLayer = (window as any).dataLayer || []
    ;(window as any)?.dataLayer?.push(dataToSend)
}

onMounted(() => {
    if (import.meta.client) {
        isDesktop.value = window.innerWidth >= 768

        setTimeout(() => {
            sendDataLayerReadyEvent()
        }, 0)
    }
})

useSeoMeta({
    ogType: 'website',
    ogLocale: useLanguage(),
    ogLocaleAlternate: locales,
    ogImage: `${APPLICATION_URL}/meta/default.jpg`,
    ogUrl: href
})
useHead({
    script: [
        {
            type: 'text/javascript',
            innerHTML:
                `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':\n` +
                `new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],\n` +
                `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=\n` +
                `'https://msr.chargemap.com/sh26cifzaticam0.js?awl='+i.replace(/^GTM-/, '')+dl;f.parentNode.insertBefore(j,f);\n` +
                `})(window,document,'script','dataLayer','${GTM_ID}')`
        }
    ],
    noscript: [
        {
            innerHTML: `<iframe src="https://msr.chargemap.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
        }
    ]
})
</script>

<template>
    <NuxtLayout>
        <ChargemapNavigation
            :language="useLanguage()"
            :country="useCountry()"
            :s3-url="S3_LEGACY_URL as string"
            :default-avatar-path="`${S3_ASSETS_URL}/common/default-avatar.png`"
            :user-id="Number(userId) || 0"
            :user-avatar="
                useUserStore().communityUserUserData?.avatarFilename ||
                undefined
            "
            :cdn-url="config.app.cdnURL || getChargemapUrl()"
            :chargemap-url="CHARGEMAP_URL"
        />
        <div class="content-wrapper">
            <slot />
        </div>
        <ChargemapFooter
            :language="useLanguage()"
            :country="useCountry()"
            :cdn-url="config.app.cdnURL"
            :is-desktop
        />
    </NuxtLayout>
</template>
